import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { DashboardShell, PageHead } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { journeyApi, apiError } from "@/lib/journeyApi";

const input = "mt-1 w-full rounded-xl pe-surface px-3.5 py-2.5 text-base text-[#171717] outline-none focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25";
const numv = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : 0; };

// Each option declares how it is handled. Most reuse existing flows (event
// adjustments or Foundation edits); a few debt-reality changes use the
// non-fabricating /me/life-event endpoint. "scope" surfaces JUST THIS TIME vs
// ONGOING only where it is meaningful.
const OPTIONS = [
  { key: "unexpected_expense", label: "Unexpected expense", scope: true,
    once: { route: "/dashboard/money-landed", collect: { field: "extra_expense", name: "Unexpected expense" }, note: "We'll carry this as a one-time expense into your next move." },
    ongoing: { route: "/dashboard/foundation/essentials?from=life", note: "Add it as a recurring essential so every future move accounts for it." } },
  { key: "income_changed", label: "Income changed", scope: true,
    once: { route: "/dashboard/money-landed", collect: { field: "additional_income", name: "Income change" }, note: "We'll carry this extra income into your next move. Your baseline stays the same." },
    ongoing: { route: "/dashboard/foundation/income?from=life", note: "Update your income baseline going forward." } },
  { key: "using_savings", label: "Using savings", scope: false,
    once: { route: "/dashboard/foundation/protection?from=life", note: "Update your Protected Runway to match what you actually have now." } },
  { key: "new_debt", label: "New debt", scope: false, lifeEvent: "new_debt" },
  { key: "overspending", label: "Overspending", scope: true,
    once: { route: "/dashboard/money-landed", collect: { field: "living_override", name: "Everyday spending" }, note: "We'll adjust everyday spending for your next move only." },
    ongoing: { route: "/dashboard/foundation/living?from=life", note: "Raise your everyday spending baseline going forward." } },
  { key: "upcoming_large_bill", label: "Upcoming large bill", scope: true,
    once: { route: "/dashboard/money-landed", collect: { field: "extra_expense", name: "Upcoming large bill" }, note: "We'll carry this as a one-time expense so your next move reserves for it." },
    ongoing: { route: "/dashboard/foundation/essentials?from=life", note: "Add it as a recurring essential." } },
  { key: "debt_balance_changed", label: "Debt balance changed outside the app", scope: false, lifeEvent: "debt_balance_changed" },
  { key: "debt_paid_off", label: "Debt paid off outside the app", scope: false, lifeEvent: "debt_paid_off" },
  { key: "expected_income", label: "Expected income changed or did not arrive", scope: false,
    once: { route: "/dashboard/expected-income", note: "Update or remove the expected income you were planning for." } },
  { key: "recurring_bill", label: "Recurring bill changed", scope: true,
    once: { route: "/dashboard/money-landed", collect: { field: "extra_expense", name: "Bill change" }, note: "We'll carry this one-time bill change into your next move." },
    ongoing: { route: "/dashboard/foundation/essentials?from=life", note: "Update the bill amount going forward." } },
  { key: "something_else", label: "Something else", scope: false,
    once: { route: "/dashboard/help", note: "Tell us what changed and we'll help you update it." } },
];

export default function LifeHappened() {
  const navigate = useNavigate();
  const [sel, setSel] = useState(null);
  const [scope, setScope] = useState("once");
  const [debts, setDebts] = useState([]);
  const [busy, setBusy] = useState(false);
  // debt-reality inputs
  const [debtId, setDebtId] = useState("");
  const [newBalance, setNewBalance] = useState("");
  const [nd, setNd] = useState({ name: "", balance: "", minimum_payment: "" });
  const [amount, setAmount] = useState("");
  const detailRef = useRef(null);

  // B10: when a selection reveals its inputs, bring that panel (the IS THIS
  // CHANGE… controls, scope toggles, inputs and the final action) into view
  // instead of jumping to the top. DashboardShell already reserves bottom space
  // so the persistent mobile nav never covers the Continue button.
  useEffect(() => {
    if (sel && detailRef.current) {
      const t = setTimeout(() => {
        try { detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) {}
      }, 60);
      return () => clearTimeout(t);
    }
  }, [sel, scope]);
  useEffect(() => {
    journeyApi.sprintState().then((s) => setDebts(s.debts || [])).catch(() => {});
  }, []);

  const option = OPTIONS.find((o) => o.key === sel) || null;
  const eligibleDebts = debts.filter((d) => !d.eliminated);

  const proceed = async () => {
    if (!option) return;
    // Debt-reality changes use the non-fabricating life-event endpoint.
    if (option.lifeEvent) {
      setBusy(true);
      try {
        let payload = { type: option.lifeEvent };
        if (option.lifeEvent === "new_debt") {
          if (!nd.name.trim() || numv(nd.balance) <= 0) { toast.error("Enter a name and balance."); setBusy(false); return; }
          payload = { ...payload, name: nd.name.trim(), balance: numv(nd.balance), minimum_payment: numv(nd.minimum_payment) };
        } else {
          if (!debtId) { toast.error("Select which debt changed."); setBusy(false); return; }
          payload.debt_id = debtId;
          if (option.lifeEvent === "debt_balance_changed") {
            if (newBalance === "" || numv(newBalance) < 0) { toast.error("Enter the new balance."); setBusy(false); return; }
            payload.new_balance = numv(newBalance);
          }
        }
        const res = await journeyApi.lifeEvent(payload);
        toast.success(option.lifeEvent === "debt_paid_off" ? "Updated. That debt is marked paid off." : "Updated your plan with the change.");
        if (res.comeback_awarded) toast.message("Comeback recorded 👋", { description: "Life interrupted the plan. You came back. That counts." });
        navigate("/dashboard");
      } catch (e) { toast.error(apiError(e, "Could not apply that change.")); }
      finally { setBusy(false); }
      return;
    }
    // Everything else routes into an existing flow (event adjustment / Foundation edit).
    const target = (option.scope && scope === "ongoing" ? option.ongoing : option.once);
    // "This time" money changes collect the amount here and hand it to the Money
    // Landed flow as a one-time event adjustment (never a Foundation change).
    if (target?.collect) {
      const amt = numv(amount);
      if (amt <= 0) { toast.error("Enter an amount to continue."); return; }
      const adj = { additional_income: "", extra_expense: "", extra_expense_name: "", living_override: "", bill_essential_id: "", bill_amount: "", persist: false };
      if (target.collect.field === "living_override") adj.living_override = String(amt);
      else if (target.collect.field === "additional_income") adj.additional_income = String(amt);
      else { adj.extra_expense = String(amt); adj.extra_expense_name = target.collect.name; }
      try { sessionStorage.setItem("ms_money_landed_draft", JSON.stringify({ adj, change: "yes" })); } catch (_) {}
      toast.message("Okay, life happened.", { description: "We'll apply this to your next move." });
      navigate(target.route);
      return;
    }
    if (target?.note) toast.message("Okay, life happened.", { description: target.note });
    navigate(target.route);
  };

  return (
    <DashboardShell>
      <button onClick={() => navigate("/dashboard")} data-testid="life-back" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>
      <PageHead title="Okay. Life happened." sub="Let's update the move. Tell us what changed and we'll recalculate from your real numbers. Nothing here is a reset, and nothing changes a payment you already confirmed." />

      <div className="space-y-2" data-testid="life-options">
        {OPTIONS.map((o) => (
          <button key={o.key} type="button" data-testid={`life-opt-${o.key}`} onClick={() => { setSel(o.key); setScope("once"); setDebtId(""); setNewBalance(""); setAmount(""); setNd({ name: "", balance: "", minimum_payment: "" }); }}
            className={`w-full rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition ${sel === o.key ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] bg-white text-[#70584A] hover:border-[#C9A86A]"}`}>
            {o.label}
          </button>
        ))}
      </div>

      {option && (
        <div ref={detailRef} className="mt-5 scroll-mt-20 rounded-2xl pe-surface p-5 pb-6" data-testid="life-detail">
          {option.scope && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Is this change…</p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" data-testid="life-scope-once" onClick={() => setScope("once")}
                  className={`min-h-11 rounded-xl border-2 px-3 text-sm font-semibold transition ${scope === "once" ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] text-[#70584A]"}`}>JUST THIS TIME</button>
                <button type="button" data-testid="life-scope-ongoing" onClick={() => setScope("ongoing")}
                  className={`min-h-11 rounded-xl border-2 px-3 text-sm font-semibold transition ${scope === "ongoing" ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] text-[#70584A]"}`}>ONGOING</button>
              </div>
              <p className="mt-2 text-xs text-[#A89A88]">Temporary by default. Ongoing updates your Foundation going forward.</p>
            </div>
          )}

          {option.lifeEvent === "new_debt" && (
            <div className="space-y-3" data-testid="life-new-debt">
              <p className="text-sm text-[#70584A]">Adding a new debt updates your plan. It does not create a payment or a completed Edit.</p>
              <div><label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Name</label>
                <input value={nd.name} onChange={(e) => setNd({ ...nd, name: e.target.value })} data-testid="life-nd-name" className={input} placeholder="e.g. Store card" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Balance</label>
                <input inputMode="decimal" value={nd.balance} onChange={(e) => setNd({ ...nd, balance: e.target.value.replace(/[^0-9.]/g, "") })} data-testid="life-nd-balance" className={input} placeholder="$0" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Minimum payment (optional)</label>
                <input inputMode="decimal" value={nd.minimum_payment} onChange={(e) => setNd({ ...nd, minimum_payment: e.target.value.replace(/[^0-9.]/g, "") })} data-testid="life-nd-min" className={input} placeholder="$0" /></div>
            </div>
          )}

          {(option.lifeEvent === "debt_balance_changed" || option.lifeEvent === "debt_paid_off") && (
            <div className="space-y-3" data-testid="life-debt-pick">
              <p className="text-sm text-[#70584A]">This updates your real balance. It never records a Paycheck Edit payment or a completed Edit for money the app did not move.</p>
              <div><label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">Which debt?</label>
                <select value={debtId} onChange={(e) => setDebtId(e.target.value)} data-testid="life-debt-select" className={input}>
                  <option value="">Select a debt</option>
                  {eligibleDebts.map((d) => <option key={d.id || d.name} value={d.id || d.name}>{d.name} · ${Number(d.balance).toLocaleString()}</option>)}
                </select></div>
              {option.lifeEvent === "debt_balance_changed" && (
                <div><label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">New balance</label>
                  <input inputMode="decimal" value={newBalance} onChange={(e) => setNewBalance(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="life-new-balance" className={input} placeholder="$0" /></div>
              )}
            </div>
          )}

          {!option.lifeEvent && (() => {
            const t = (option.scope && scope === "ongoing" ? option.ongoing : option.once);
            return (
              <div>
                <p className="text-sm text-[#70584A]" data-testid="life-route-note">{t?.note}</p>
                {t?.collect && (
                  <div className="mt-3">
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70584A]">{t.collect.field === "additional_income" ? "How much extra came in?" : t.collect.field === "living_override" ? "Everyday spending for this move" : "How much is it?"}</label>
                    <input inputMode="decimal" value={amount} autoFocus onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="life-amount" className={input} placeholder="$0" />
                    <p className="mt-2 text-xs text-[#A89A88]">Enter only what is genuinely available/affected. This applies to your next move only.</p>
                  </div>
                )}
              </div>
            );
          })()}

          <Button data-testid="life-proceed" disabled={busy} onClick={proceed} className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] font-semibold text-white hover:bg-[#255643]">
            {busy ? "Updating…" : "Continue"} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      )}
    </DashboardShell>
  );
}
